import { KEYCLOAK_CLIENT_ID, KEYCLOAK_REALM, KEYCLOAK_URL } from "@/Constants";
import Keycloak from "keycloak-js";

// Keycloak initialization options
const keycloakConfig = {
  url: KEYCLOAK_URL, // Your Keycloak server URL
  realm: KEYCLOAK_REALM, // Your realm name
  clientId: KEYCLOAK_CLIENT_ID, // Your client ID
};

// Initialize Keycloak instance
const keycloak = new Keycloak(keycloakConfig);

// Track initialization state
let initialized = false;
let initPromise: Promise<void> | null = null;

// Event listeners
type KeycloakEventCallback = () => void;
const eventListeners: Record<string, KeycloakEventCallback[]> = {
  onAuthSuccess: [],
  onAuthError: [],
  onAuthRefreshSuccess: [],
  onAuthRefreshError: [],
  onAuthLogout: [],
  onTokenExpired: [],
};

// Initialize Keycloak
const initKeycloak = (onAuthenticatedCallback: () => void) => {
  if (initialized) {
    onAuthenticatedCallback();
    return;
  }

  if (!initPromise) {
    initPromise = keycloak
      .init({
        onLoad: "check-sso",
        silentCheckSsoRedirectUri:
          window.location.origin + "/silent-check-sso.html",
        pkceMethod: "S256",
        checkLoginIframe: false, // Disable iframe checking which can cause issues
      })
      .then((authenticated) => {
        initialized = true;
        if (authenticated) {
          console.debug("User is authenticated");
        } else {
          console.debug("User is not authenticated");
        }
        onAuthenticatedCallback();
      })
      .catch((error) => {
        console.error("Failed to initialize Keycloak", error);
      });
  } else {
    initPromise.then(onAuthenticatedCallback);
  }

  // Setup event listeners
  keycloak.onAuthSuccess = () => {
    console.debug("Auth success");
    eventListeners.onAuthSuccess.forEach((cb) => cb());
  };

  keycloak.onAuthError = () => {
    console.debug("Auth error");
    eventListeners.onAuthError.forEach((cb) => cb());
  };

  keycloak.onAuthRefreshSuccess = () => {
    console.debug("Auth refresh success");
    eventListeners.onAuthRefreshSuccess.forEach((cb) => cb());
  };

  keycloak.onAuthRefreshError = () => {
    console.debug("Auth refresh error");
    eventListeners.onAuthRefreshError.forEach((cb) => cb());
  };

  keycloak.onAuthLogout = () => {
    console.debug("Auth logout");
    eventListeners.onAuthLogout.forEach((cb) => cb());
  };

  keycloak.onTokenExpired = () => {
    console.debug("Token expired");
    eventListeners.onTokenExpired.forEach((cb) => cb());
    keycloak.updateToken(30).catch(() => {
      console.debug("Failed to refresh token, logging out");
      keycloak.logout();
    });
  };
};

// Add event listener
const addEventListener = (event: string, callback: KeycloakEventCallback) => {
  if (eventListeners[event]) {
    eventListeners[event].push(callback);
  }
};

// Remove event listener
const removeEventListener = (
  event: string,
  callback: KeycloakEventCallback
) => {
  if (eventListeners[event]) {
    eventListeners[event] = eventListeners[event].filter(
      (cb) => cb !== callback
    );
  }
};

// Login function
const doLogin = (options: { redirectUri?: string } = {}) => {
  const redirectUri = options.redirectUri || window.location.origin;
  keycloak.login({ redirectUri });
};

// Logout function
const doLogout = (options: { redirectUri?: string } = {}) => {
  const redirectUri = options.redirectUri || window.location.origin;
  keycloak.logout({ redirectUri });
};

// Check if authenticated
const isAuthenticated = () => !!keycloak.authenticated;

// Get the token
const getToken = () => keycloak.token;

// Get username
const getUsername = () => keycloak.tokenParsed?.preferred_username;

// Update token periodically
const updateToken = (successCallback: () => void) => {
  keycloak
    .updateToken(5)
    .then(successCallback)
    .catch(() => doLogin());
};

// Check if user has a specific role
const hasRole = (roles: string[]) => {
  return roles.some((role) => {
    return keycloak.hasRealmRole(role);
  });
};

const KeycloakService = {
  keycloak,
  initKeycloak,
  doLogin,
  doLogout,
  isAuthenticated,
  getToken,
  getUsername,
  updateToken,
  hasRole,
  addEventListener,
  removeEventListener,
};

export default KeycloakService;
